import { useCallback, useEffect, useState } from "react";
import { Star } from "lucide-react";
import { adminApi, ApiError, type AdminReviewRecord, type AdminReviewReport } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { getAdminStatusTone } from "./status-tone";

function formatDateTime(value: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function StarRating({ stars }: { stars: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${stars} star rating`}>
      {Array.from({ length: 5 }).map((_, index) => (
        <Star
          key={index}
          className={`size-4 ${
            index < stars ? "fill-amber-400 text-amber-400" : "text-[var(--color-text-soft)]"
          }`}
        />
      ))}
    </span>
  );
}

interface ReviewModerationPanelProps {
  onModerated?: () => void;
  /** Show only pending reviews, only UGC reports, or both. */
  show?: "reviews" | "reports" | "all";
}

export function ReviewModerationPanel({ onModerated, show = "all" }: ReviewModerationPanelProps) {
  const showReviews = show !== "reports";
  const showReports = show !== "reviews";
  const { t } = useAppPreferences();
  const [reviews, setReviews] = useState<AdminReviewRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({});
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [reports, setReports] = useState<AdminReviewReport[]>([]);
  const [reportRationales, setReportRationales] = useState<Record<string, string>>({});
  // Reviews decided during this session, kept locally so they don't just vanish.
  const [decidedReviews, setDecidedReviews] = useState<AdminReviewRecord[]>([]);
  const [showDecided, setShowDecided] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [response, reportResponse] = await Promise.all([adminApi.listPendingReviews(), adminApi.listPendingReviewReports()]);
      setReviews(response.reviews);
      setReports(reportResponse ?? []);
      setReviewNotes((current) => {
        const next = { ...current };
        for (const review of response.reviews) {
          if (!(review.id in next)) {
            next[review.id] = review.review_note ?? "";
          }
        }
        return next;
      });
    } catch (requestError) {
      setError(
        requestError instanceof ApiError
          ? requestError.detail
          : t("admin.reviews.error"),
      );
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleApprove = async (reviewId: string) => {
    setProcessingId(reviewId);
    setError(null);
    setNotice(null);
    try {
      const decided = reviews.find((review) => review.id === reviewId);
      await adminApi.updateReviewStatus(reviewId, {
        decision: "approve",
        review_note: reviewNotes[reviewId]?.trim() || null,
      });
      if (decided) {
        setDecidedReviews((current) => [
          { ...decided, status: "approved", review_note: reviewNotes[reviewId]?.trim() || null },
          ...current,
        ]);
      }
      setNotice(t("admin.reviews.approvedNotice"));
      await load();
      onModerated?.();
    } catch (requestError) {
      setError(
        requestError instanceof ApiError
          ? requestError.detail
          : t("admin.reviews.error"),
      );
    } finally {
      setProcessingId(null);
    }
  };

  const resolveReport = async (reportId: string, decision: "dismiss" | "remove_review") => {
    setProcessingId(reportId); setError(null);
    const rationale = reportRationales[reportId]?.trim();
    if (!rationale) { setProcessingId(null); return; }
    try { await adminApi.resolveReviewReport(reportId, { decision, rationale }); setNotice(t("admin.reports.resolved")); await load(); onModerated?.(); }
    catch (requestError) { setError(requestError instanceof ApiError ? requestError.detail : t("admin.reports.error")); }
    finally { setProcessingId(null); }
  };

  const handleReject = async (reviewId: string) => {
    const reviewNote = reviewNotes[reviewId]?.trim();
    if (!reviewNote) {
      setError(t("admin.reviews.rejectionNoteRequired"));
      return;
    }

    setProcessingId(reviewId);
    setError(null);
    setNotice(null);
    try {
      const decided = reviews.find((review) => review.id === reviewId);
      await adminApi.updateReviewStatus(reviewId, {
        decision: "reject",
        review_note: reviewNote,
      });
      if (decided) {
        setDecidedReviews((current) => [
          { ...decided, status: "rejected", review_note: reviewNote },
          ...current,
        ]);
      }
      setNotice(t("admin.reviews.rejectedNotice"));
      await load();
      onModerated?.();
    } catch (requestError) {
      setError(
        requestError instanceof ApiError
          ? requestError.detail
          : t("admin.reviews.error"),
      );
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="admin-page admin-reviews mx-auto max-w-5xl space-y-5">
      {showReviews ? <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-text-soft)]">
            {t("admin.reviews.eyebrow")}
          </p>
          <h2 className="mt-1 text-xl font-semibold text-[var(--color-text)]">
            {t("admin.reviews.title")}
          </h2>
        </div>
        <span className="rounded-full border border-[var(--color-border-sub)] bg-[var(--color-surface)] px-3 py-1 text-xs text-[var(--color-text)]">
          {t(reviews.length === 1 ? "{count} review" : "{count} reviews", { count: reviews.length })}
        </span>
      </div> : null}

      {(notice || error) && (
        <div className="space-y-2">
          {notice && (
            <div className="flex items-center justify-between rounded-lg border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-sm text-[var(--color-success)]">
              <span>{notice}</span>
              <button
                aria-label={t("Dismiss success message")}
                className="ops-button"
                data-size="sm"
                data-variant="secondary"
                onClick={() => setNotice(null)}
                type="button"
              >
                ✕
              </button>
            </div>
          )}
          {error && (
            <div className="flex items-center justify-between rounded-lg border border-rose-400/20 bg-rose-400/10 px-4 py-2 text-sm text-[var(--color-error)]">
              <span>{error}</span>
              <button
                aria-label={t("Dismiss error message")}
                className="ops-button"
                data-size="sm"
                data-variant="secondary"
                onClick={() => setError(null)}
                type="button"
              >
                ✕
              </button>
            </div>
          )}
        </div>
      )}

      {!showReviews ? null : isLoading ? (
        <p className="text-sm text-[var(--color-text-soft)]">{t("admin.reviews.loading")}</p>
      ) : reviews.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border-sub)] px-5 py-8 text-sm text-[var(--color-text-soft)]">
          {t("admin.reviews.empty")}
        </div>
      ) : (
        <div className="grid gap-2">
          {reviews.map((review) => {
            const isProcessing = processingId === review.id;

            return (
              <div
                className="rounded-xl border border-[var(--color-border-sub)] bg-[var(--color-surface)] p-3"
                key={review.id}
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="min-w-0 max-w-full truncate text-sm font-semibold text-[var(--color-text)]">
                        {review.venue_name}
                      </h3>
                      <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase ${getAdminStatusTone(review.status)}`}>
                        {review.status}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-sm text-[var(--color-text-soft)]">
                      {review.customer_name ?? review.customer_email ?? "—"}
                      {review.customer_name && review.customer_email
                        ? ` · ${review.customer_email}`
                        : null}
                    </p>
                    <p className="mt-1 text-xs text-[var(--color-text-soft)]">
                      {t("admin.reviews.submitted")} {formatDateTime(review.created_at)}
                    </p>
                  </div>
                  <div className="shrink-0">
                    <StarRating stars={review.stars} />
                  </div>
                </div>

                {review.comment ? (
                  <div className="mt-2 rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)]">
                    <span className="text-xs text-[var(--color-text-soft)]">
                      {t("admin.reviews.comment")}:
                    </span>{" "}
                    <span className="whitespace-pre-wrap">{review.comment}</span>
                  </div>
                ) : (
                  <p className="mt-2 text-sm italic text-[var(--color-text-soft)]">
                    {t("admin.reviews.noComment")}
                  </p>
                )}

                <div className="mt-3 grid gap-2 sm:flex sm:items-end sm:gap-3">
                  <textarea
                    className="ops-input min-h-[60px] flex-1 text-sm"
                    onChange={(event) =>
                      setReviewNotes((current) => ({
                        ...current,
                        [review.id]: event.target.value,
                      }))
                    }
                    placeholder={t("admin.reviews.reviewNote")}
                    rows={2}
                    value={reviewNotes[review.id] ?? ""}
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      className="rounded-lg bg-emerald-500/80 px-3 py-2 text-xs font-semibold text-white transition hover:bg-emerald-500 disabled:opacity-50"
                      disabled={isProcessing}
                      onClick={() => void handleApprove(review.id)}
                      type="button"
                    >
                      {isProcessing ? t("admin.reviews.processing") : t("admin.reviews.approve")}
                    </button>
                    <button
                      className="rounded-lg bg-rose-500/80 px-3 py-2 text-xs font-semibold text-white transition hover:bg-rose-500 disabled:opacity-50"
                      disabled={isProcessing}
                      onClick={() => void handleReject(review.id)}
                      type="button"
                    >
                      {isProcessing ? t("admin.reviews.processing") : t("admin.reviews.reject")}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {showReports ? <section className={showReviews ? "border-t border-[var(--color-border-sub)] pt-6" : ""}>
        <div className="flex items-end justify-between gap-3"><div><p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-text-soft)]">{t("admin.reports.eyebrow")}</p><h2 className="mt-1 text-xl font-semibold text-[var(--color-text)]">{t("admin.reports.title")}</h2></div><span className="rounded-full border border-rose-300/20 bg-rose-300/10 px-3 py-1 text-xs text-[var(--color-error)]">{reports.length}</span></div>
         <div className="mt-4 grid gap-3">
           {reports.length === 0 ? <p className="text-sm text-[var(--color-text-soft)]">{t("admin.reports.empty")}</p> : reports.map((report) => {
             const evidence = report.evidence_snapshot;
             const currentReview = report.current_review;
             const isProcessing = processingId === report.id;
             return (
               <div className="rounded-xl border border-rose-300/15 bg-rose-300/[0.04] p-4" key={report.id}>
                 <div className="flex flex-wrap justify-between gap-2"><p className="text-sm font-semibold text-[var(--color-text)]">{report.target} · {report.reason}</p><span className="text-xs text-[var(--color-text-soft)]">{formatDateTime(report.created_at)}</span></div>
                 <div className="mt-3 grid gap-2 rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-surface)] p-3 text-sm text-[var(--color-text)] sm:grid-cols-2">
                   <p><span className="text-[var(--color-text-soft)]">{t("admin.reports.venue")}:</span> {report.venue_name} <span className="font-mono text-[var(--color-text-soft)]" title={report.venue_id}>({report.venue_id.slice(0, 8)})</span></p>
                   <p><span className="text-[var(--color-text-soft)]">{t("admin.reports.stars")}:</span> {String(evidence.stars ?? "—")}</p>
                   <p className="sm:col-span-2"><span className="text-[var(--color-text-soft)]">{t("admin.reports.comment")}:</span> <span className="whitespace-pre-wrap">{String(evidence.comment ?? "—")}</span></p>
                   <p><span className="text-[var(--color-text-soft)]">{t("admin.reports.reviewChanged")}:</span> {String(evidence.review_updated_at ?? "—")}</p>
                   <p><span className="text-[var(--color-text-soft)]">{t("admin.reports.currentState")}:</span> {currentReview?.status ?? t("admin.reports.noCurrentReview")}</p>
                   {currentReview ? <p className="sm:col-span-2"><span className="text-[var(--color-text-soft)]">{t("admin.reports.currentReview")}:</span> {currentReview.stars} ★ · {currentReview.comment || "—"} · {formatDateTime(currentReview.updated_at)}</p> : null}
                 </div>
                 {report.stale ? <p className="mt-2 rounded-md border border-amber-300/20 bg-amber-300/10 px-3 py-2 text-xs text-[var(--color-amber)]">{t("admin.reports.stale")}</p> : null}
                 <p className="mt-2 text-sm text-[var(--color-text-soft)]">{report.details || "—"}</p>
                 <textarea className="ops-input mt-3 min-h-[60px] w-full text-sm" value={reportRationales[report.id] ?? ""} onChange={(event) => setReportRationales((current) => ({ ...current, [report.id]: event.target.value }))} placeholder={t("admin.reports.rationalePlaceholder")} rows={2} />
                 <div className="mt-3 flex flex-wrap gap-2"><button className="rounded-lg bg-slate-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50" disabled={isProcessing || !reportRationales[report.id]?.trim()} onClick={() => void resolveReport(report.id, "dismiss")} type="button">{t("admin.reports.dismiss")}</button>{!report.stale ? <button className="rounded-lg bg-rose-500/80 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50" disabled={isProcessing || !reportRationales[report.id]?.trim()} onClick={() => void resolveReport(report.id, "remove_review")} type="button">{t("admin.reports.remove")}</button> : null}</div>
               </div>
             );
           })}
         </div></section> : null}
      {decidedReviews.length > 0 && (
        <section className="border-t border-[var(--color-border-sub)] pt-4">
          <button
            className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-text-soft)] transition hover:text-[var(--color-text)]"
            onClick={() => setShowDecided((current) => !current)}
            type="button"
          >
            <span>{showDecided ? "▾" : "▸"}</span>
            {t("Decided this session ({count})", { count: decidedReviews.length })}
          </button>
          {showDecided && (
            <div className="mt-2 grid gap-2">
              {decidedReviews.map((review) => (
                <div
                  className="rounded-xl border border-[var(--color-border-sub)] bg-[var(--color-surface)] p-3 opacity-60"
                  data-status={review.status}
                  key={review.id}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="min-w-0 max-w-full truncate text-sm font-semibold text-[var(--color-text)]">
                      {review.venue_name}
                    </h3>
                    <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase ${getAdminStatusTone(review.status)}`}>
                      {review.status}
                    </span>
                    <StarRating stars={review.stars} />
                  </div>
                  {review.review_note ? (
                    <p className="mt-1 text-xs text-[var(--color-text-soft)]">{t("Note: {note}", { note: review.review_note })}</p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
